import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  FavoriteBorder,
  Home,
  Logout,
  Menu,
  PersonOutline,
  Search,
  Wallet,
  Settings,
  VerifiedUser,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import HeaderProfileMenu from '../components/HeaderProfileMenu';
import '../styles/maandvo-dashboard.css';

const MAROON = '#7b0011';

interface ActivationStatus {
  status: string;
  activation_started_at?: string | null;
  activation_expires_at?: string | null;
  days_remaining: number;
  is_expired: boolean;
  reactivation_requested_at?: string | null;
  lead_cost_inr: number;
  default_lead_cost_inr: number;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const ActivationStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [activation, setActivation] = useState<ActivationStatus | null>(null);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/me/activation-status');
      setActivation(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load activation status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadStatus();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const requestReactivation = async () => {
    try {
      setRequesting(true);
      await api.post('/users/me/request-reactivation', {});
      toast.success('Reactivation request sent to admin');
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to request reactivation');
    } finally {
      setRequesting(false);
    }
  };

  const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    if (status === 'ACTIVE') return 'success';
    if (status === 'REACTIVATION_REQUESTED') return 'warning';
    if (status === 'EXPIRED') return 'error';
    return 'default';
  };

  const sidebarItems = useMemo(
    () => [
      { label: 'Dashboard', icon: <Home fontSize="small" />, action: () => navigate('/my-profile') },
      { label: 'Update Profile', icon: <PersonOutline fontSize="small" />, action: () => navigate('/my-profile?openCompleteProfile=1') },
      { label: 'Match Preferences', icon: <Settings fontSize="small" />, action: () => navigate('/my-profile?openMatchPreferences=1') },
      { label: 'Search Profiles', icon: <Home fontSize="small" />, action: () => navigate('/') },
      { label: 'Unlocked Profiles', icon: <FavoriteBorder fontSize="small" />, action: () => navigate('/my-connections') },
      { label: 'Recharge Account', icon: <Wallet fontSize="small" />, action: () => navigate('/wallet') },
      { label: 'Activation Status', icon: <VerifiedUser fontSize="small" />, action: () => navigate('/activation-status'), active: true },
      { label: 'Change Password', icon: <Settings fontSize="small" />, action: () => navigate('/change-password') },
      { label: 'Logout', icon: <Logout fontSize="small" />, action: handleLogout },
    ],
    [navigate]
  );

  const sidebarContent = (
    <Box className="maandvo-dashboard-sidebar" role="presentation">
      <Box className="maandvo-dashboard-brand">
        <Box component="img" src="/maandvo-logo.png" alt="Brand" />
      </Box>
      <Box className="maandvo-dashboard-nav">
        {sidebarItems.map((item) => (
          <Box key={item.label} className={`maandvo-dashboard-nav-item ${item.active ? 'active' : ''}`} onClick={item.action}>
            {item.icon}
            <span>{item.label}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box className="maandvo-dashboard">
      <Box className="maandvo-dashboard-mobile-top">
        <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}>
          <Menu />
        </IconButton>
        <Typography sx={{ fontWeight: 800 }}>Activation Status</Typography>
        <HeaderProfileMenu
          initial={user?.first_name?.[0] || 'U'}
          actions={[
            { label: 'Dashboard', onClick: () => navigate('/my-profile') },
            { label: 'Logout', onClick: handleLogout, icon: <Logout fontSize="small" />, color: '#b71c1c' },
          ]}
        />
      </Box>

      <Box className="maandvo-dashboard-layout">
        <Box className="maandvo-dashboard-sidebar desktop-only">{sidebarContent}</Box>
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ width: 280 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={() => setMobileOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            {sidebarContent}
          </Box>
        </Drawer>

        <Box className="maandvo-dashboard-main">
          {loading ? (
            <Box sx={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: MAROON }} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: MAROON, mb: 1 }}>
                    Profile Activation Lifecycle
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your profile stays active for 1 year after admin approval. After expiry, request reactivation.
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Current Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip color={statusColor(activation?.status || 'PENDING_APPROVAL')} label={(activation?.status || 'PENDING_APPROVAL').replace(/_/g, ' ')} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Lead Cost</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{activation?.lead_cost_inr ?? 10} Points per lead</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Activated On</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{formatDate(activation?.activation_started_at)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Valid Till</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{formatDate(activation?.activation_expires_at)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Days Remaining</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{activation?.days_remaining ?? 0}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Last Reactivation Request</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{formatDate(activation?.reactivation_requested_at)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {(activation?.status === 'EXPIRED' || activation?.status === 'PENDING_APPROVAL') && (
                <Alert
                  severity={activation?.status === 'EXPIRED' ? 'warning' : 'info'}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={requestReactivation}
                      disabled={requesting}
                    >
                      {requesting ? 'Sending...' : 'Request Reactivation'}
                    </Button>
                  }
                >
                  {activation?.status === 'EXPIRED'
                    ? 'Your 1-year activation has expired. Request admin approval to reactivate your profile.'
                    : 'Your profile is pending admin activation. Send a request to notify admin.'}
                </Alert>
              )}

              {activation?.status === 'REACTIVATION_REQUESTED' && (
                <Alert severity="info">Your reactivation request is pending admin approval.</Alert>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ActivationStatusPage;
